import { ref } from 'vue'
import { useSubscription } from './useSubscription.js'

// Module-level state shared across all call sites — one fetch per navigation cycle.
const isAuthenticated = ref(false)
const currentUser = ref(null)
let authChecked = false
let pendingCheck = null

const { setSubscription } = useSubscription()

const fetchAuthState = async () => {
  try {
    const response = await fetch('/api/users/current', {
      headers: { 'Accept': 'application/json' }
    })
    const data = await response.json()
    if (response.ok && data.success) {
      isAuthenticated.value = true
      currentUser.value = data.user
      // Keep the shared subscription store in sync so isPro/cv limits are
      // correct everywhere (UserDropdown, dashboard usage card, …).
      if (data.user?.subscription) setSubscription(data.user.subscription)
    } else {
      isAuthenticated.value = false
      currentUser.value = null
    }
  } catch {
    isAuthenticated.value = false
    currentUser.value = null
  } finally {
    authChecked = true
    pendingCheck = null
  }
}

export function useAuth() {
  // Returns the cached state immediately; triggers a fresh fetch only once
  // per page load unless invalidated by login/logout.
  const checkAuthStatus = async ({ force = false } = {}) => {
    if (force || !authChecked) {
      if (!pendingCheck) {
        pendingCheck = fetchAuthState()
      }
      await pendingCheck
    }
  }

  const handleLogout = async () => {
    try {
      const response = await fetch('/api/users/signout', {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })
      if (response.ok) {
        isAuthenticated.value = false
        currentUser.value = null
        authChecked = false
        localStorage.removeItem('cv-tool.com_autosave')
        sessionStorage.removeItem('cv_parser_result')
        window.location.href = '/'
      } else {
        console.error('Logout failed')
      }
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  // Call after a successful login so the next guard picks up the new state.
  const invalidateAuthCache = () => {
    authChecked = false
  }

  return {
    isAuthenticated,
    currentUser,
    checkAuthStatus,
    handleLogout,
    invalidateAuthCache
  }
}
